import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { Chat, Message, AIModel, Project, MCPService, Attachment, SubscriptionTier, Agent, Blueprint } from '@/types';
import { Database } from '@/integrations/supabase/types';
import { toast } from '@/components/ui/use-toast';
import { fetchOllamaModels } from '@/lib/ollama';
import { useAppStore } from '@/store';
import { v4 as uuidv4 } from 'uuid';
import { trackEvent } from '@/lib/analytics';
import { appendChatMessageToVFS, saveChatAttachmentsToVFS, renameChatVFSFolder } from '@/lib/osVFSBridge';

type ChatWithMessages = Chat & { messages: Message[] };
export type AgentWithBlueprints = Agent & { blueprints: Pick<Blueprint, 'id' | 'name' | 'data'>[] };

interface SupabaseState {
  chats: ChatWithMessages[];
  models: AIModel[];
  projects: Project[];
  agents: AgentWithBlueprints[];
  refreshChats: () => Promise<void>;
  fetchChats: (userId: string) => Promise<void>;
  fetchChatMessages: (chatId: string) => Promise<void>;
  addChat: (userId: string, title: string, modelId: string, projectId?: string) => Promise<Chat | null>;
  addMessage: (chatId: string, message: Partial<Message>) => Promise<Message | null>;
  addMessageLocally: (chatId: string, message: Message) => void;
  updateMessageLocally: (chatId: string, tempId: string, newMessage: Message) => void;
  updateChatTitle: (chatId: string, newTitle: string) => Promise<void>;
  updateChat: (chatId: string, updates: Partial<Chat>) => void;
  updateChatModel: (chatId: string, newModelId: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  deleteAllChats: (userId: string) => Promise<void>;
  addTokensUsed: (chatId: string, tokens: number) => Promise<void>;
  incrementChatViews: (chatId: string) => Promise<void>;
  fetchModels: () => Promise<void>;
  updateModel: (modelId: string, updates: Partial<Database['public']['Tables']['ai_models']['Update']>) => Promise<void>;
  updateProviderModels: (provider: string, updates: Partial<Database['public']['Tables']['ai_models']['Update']>) => Promise<void>;
  fetchProjects: (userId: string) => Promise<void>;
  addProject: (name: string, description?: string) => Promise<Project | null>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  assignChatToProject: (chatId: string, projectId: string | null) => Promise<void>;
  toggleFavoriteChat: (chatId: string) => Promise<void>;
  fetchSingleChat: (chatId: string) => Promise<ChatWithMessages | null>;
  // New MCP Service functions
  fetchMCPServices: () => Promise<MCPService[]>;
  addMCPService: (service: Omit<MCPService, "id" | "created_at" | "created_by">) => Promise<string | null>;
  updateMCPService: (id: string, updates: Partial<MCPService>) => Promise<void>;
  deleteMCPService: (id: string) => Promise<void>;
  getUserMCPApiKey: (serviceId: string) => Promise<string | null>;
  setUserMCPApiKey: (serviceId: string, apiKey: string) => Promise<void>;
  // New Agent functions
  addAgent: (agent: Omit<Agent, "id" | "user_id" | "created_at" | "updated_at">, blueprintIds: string[]) => Promise<Agent | null>;
  fetchAgents: () => Promise<void>;
  updateAgentBlueprints: (agentId: string, blueprintIds: string[]) => Promise<void>;
  addBlueprint: (blueprint: Omit<Blueprint, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'is_private' | 'organization_id'>) => Promise<Blueprint | null>;
  fetchAgentById: (agentId: string) => Promise<AgentWithBlueprints | null>;
  // Operator logs
  logOperatorAction: (action: string, details?: Record<string, any>) => Promise<void>;
  fetchOperatorLogs: (limit?: number) => Promise<Array<{ id: string; action: string; details: any; created_at: string }>>;
  // NEW: pagination state and loader
  chatPage?: number;
  chatPageSize?: number;
  chatHasMore: boolean;
  loadMoreChats: () => Promise<void>;
}

export const useSupabaseStore = create<SupabaseState>((set, get) => ({
  chats: [],
  models: [],
  projects: [],
  agents: [],
  // NEW: initialize pagination
  chatPage: 0,
  chatPageSize: 20,
  chatHasMore: true,

  refreshChats: async () => {
    const { data: { session } = {} } = await supabase.auth.getSession();
    if (session?.user) {
      // Reset pagination to the first page
      set({ chatPage: 0, chatHasMore: true });
      await get().fetchChats(session.user.id);
    } else {
      console.warn("[SupabaseStore] No user session found, cannot refresh chats.");
      set({ chats: [] });
    }
  },

  fetchChats: async (userId) => {
    if (!userId) return;
    if (!useAppStore.getState().saveHistory) {
      set({ chats: [], chatHasMore: false, chatPage: 0 });
      return;
    }

    const pageSize = get().chatPageSize ?? 20;
    const from = 0;
    const to = pageSize - 1;

    const { data, error } = await supabase
      .from('chats')
      .select(`
        id,
        title,
        created_at,
        updated_at,
        model_id,
        message_count,
        tokens_used,
        project_id,
        is_favorite,
        type,
        view_count
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Error fetching chats:', error);
      toast({ title: "Error", description: "Failed to load chats.", variant: "destructive" });
      return;
    }

    set(state => {
      const newChats = (data || []).map(chat => {
        const existingChat = state.chats.find(c => c.id === chat.id);
        return {
          id: chat.id,
          title: chat.title,
          createdAt: chat.created_at,
          updatedAt: chat.updated_at,
          modelId: chat.model_id,
          messageCount: chat.message_count || 0,
          tokensUsed: chat.tokens_used || 0,
          project_id: chat.project_id,
          isFavorite: chat.is_favorite,
          type: chat.type as 'user-ai' | 'arena',
          viewCount: chat.view_count,
          messages: existingChat ? existingChat.messages : [],
          messagesLoaded: existingChat ? existingChat.messagesLoaded : false,
        };
      });

      return {
        chats: newChats,
        chatPage: 0,
        chatHasMore: (data || []).length === pageSize
      };
    });
  },

  // NEW: load more with offset pagination
  loadMoreChats: async () => {
    const { data: { session } = {} } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;

    if (!useAppStore.getState().saveHistory) return;

    const page = (get().chatPage ?? 0) + 1;
    const pageSize = get().chatPageSize ?? 20;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const { data, error } = await supabase
      .from('chats')
      .select(`
        id,
        title,
        created_at,
        updated_at,
        model_id,
        message_count,
        tokens_used,
        project_id,
        is_favorite,
        type,
        view_count
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Error loading more chats:', error);
      toast({ title: "Error", description: "Failed to load more chats.", variant: "destructive" });
      return;
    }

    set(state => {
      const appended = (data || []).map(chat => ({
        id: chat.id,
        title: chat.title,
        createdAt: chat.created_at,
        updatedAt: chat.updated_at,
        modelId: chat.model_id,
        messageCount: chat.message_count || 0,
        tokensUsed: chat.tokens_used || 0,
        project_id: chat.project_id,
        isFavorite: chat.is_favorite,
        type: chat.type as 'user-ai' | 'arena',
        viewCount: chat.view_count,
        messages: [],
        messagesLoaded: false,
      }));

      // De-duplicate by id in case of overlap
      const byId = new Map(state.chats.map(c => [c.id, c]));
      appended.forEach(c => byId.set(c.id, { ...(byId.get(c.id) || {}), ...c }));

      return {
        chats: Array.from(byId.values()).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
        chatPage: page,
        chatHasMore: (appended || []).length === pageSize
      };
    });
  },

  fetchChatMessages: async (chatId) => {
    const { data, error } = await supabase
      .from('messages')
      .select('*, attachments(*)')
      .eq('chat_id', chatId)
      .order('timestamp', { ascending: true });

    if (error) {
      console.error('Error fetching messages:', error);
      toast({ title: "Error", description: "Failed to load messages.", variant: "destructive" });
      return;
    }

    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === chatId
          ? {
              ...chat,
              messages: data.map(msg => ({
                id: msg.id,
                chatId: msg.chat_id,
                content: msg.content,
                role: msg.role as 'user' | 'assistant' | 'system',
                sender: msg.sender as 'user' | 'ai',
                timestamp: msg.timestamp,
                model: msg.model || undefined,
                attachments: msg.attachments as Attachment[] || [],
                is_arena: msg.is_arena || false,
                arena_group_id: msg.arena_group_id || undefined,
                arena_model: msg.arena_model || undefined,
                arena_order: msg.arena_order || undefined,
              })),
              messagesLoaded: true, // Mark messages as loaded
            }
          : chat
      ),
    }));
  },

  addChat: async (userId, title, modelId, projectId) => {
    console.log('addChat called with:', { userId, title, modelId, projectId });
    const saveHistory = useAppStore.getState().saveHistory;
    console.log('saveHistory setting:', saveHistory);

    if (!saveHistory) {
      const tempChat: ChatWithMessages = {
        id: uuidv4(),
        title,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        modelId,
        messageCount: 0,
        tokensUsed: 0,
        project_id: projectId || null,
        isFavorite: false,
        type: 'user-ai',
        viewCount: 0,
        messages: [],
        messagesLoaded: true, // New chat, no persisted messages yet, so locally loaded
      };
      set(state => ({ chats: [tempChat, ...state.chats] }));
      trackEvent('chat_created', { chatId: tempChat.id, projectId, saved: false });
      console.log('Returning temporary chat (saveHistory is false):', tempChat);
      return tempChat;
    }

    const { data, error } = await supabase
      .from('chats')
      .insert({ user_id: userId, title, model_id: modelId, project_id: projectId })
      .select()
      .single();

    if (error) {
      console.error('Error adding chat to Supabase:', error); // More specific error message
      toast({ title: "Error", description: "Failed to create new chat.", variant: "destructive" });
      return null;
    }

    const newChat: ChatWithMessages = {
      id: data.id,
      title: data.title,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      modelId: data.model_id,
      messageCount: 0,
      tokensUsed: 0,
      project_id: data.project_id,
      isFavorite: false,
      type: 'user-ai',
      viewCount: 0,
      messages: [],
      messagesLoaded: true, // New chat, no persisted messages yet, so locally loaded
    };

    set(state => ({ chats: [newChat, ...state.chats] }));
    trackEvent('chat_created', { chatId: newChat.id, projectId, saved: true });
    console.log('Returning Supabase chat:', newChat);
    return newChat;
  },

  addMessage: async (chatId, message) => {
    if (message.sender === 'user') {
      trackEvent('message_sent', { chatId });
    }
    const saveHistory = useAppStore.getState().saveHistory;

    if (!saveHistory) {
      const tempMessage: Message = {
        id: uuidv4(),
        chatId: chatId,
        content: message.content || '',
        role: message.role as 'user' | 'assistant' | 'system',
        sender: message.sender as 'user' | 'ai',
        timestamp: new Date().toISOString(),
        model: message.model || undefined,
        attachments: message.attachments || [],
        is_arena: message.is_arena || false,
        arena_group_id: message.arena_group_id || undefined,
        arena_model: message.arena_model || undefined,
        arena_order: message.arena_order || undefined,
      };
      get().addMessageLocally(chatId, tempMessage);
      return tempMessage;
    }

    const { data: messageResult, error: messageError } = await supabase
      .from('messages')
      .insert({
        chat_id: chatId,
        content: message.content,
        role: message.role,
        sender: message.sender,
        model: message.model,
        is_arena: message.is_arena,
        arena_group_id: message.arena_group_id,
        arena_model: message.arena_model,
        arena_order: message.arena_order,
      })
      .select()
      .single();

    if (messageError) {
      console.error('Error adding message:', messageError);
      toast({ title: "Error", description: "Failed to save message.", variant: "destructive" });
      return null;
    }

    const newMessage: Message = {
      id: messageResult.id,
      chatId: messageResult.chat_id,
      content: messageResult.content,
      role: messageResult.role as 'user' | 'assistant' | 'system',
      sender: messageResult.sender as 'user' | 'ai',
      timestamp: messageResult.timestamp,
      model: messageResult.model || undefined,
      attachments: [],
      is_arena: messageResult.is_arena || false,
      arena_group_id: messageResult.arena_group_id || undefined,
      arena_model: messageResult.arena_model || undefined,
      arena_order: messageResult.arena_order || undefined,
    };

    if (message.attachments?.length) {
      const attachmentsToInsert = message.attachments.map(att => ({
        message_id: messageResult.id,
        content: att.content,
        type: att.type,
        name: att.name || `${att.type}-${Date.now()}`,
      }));

      const { data: attachmentResults, error: attachmentError } = await supabase
        .from('attachments')
        .insert(attachmentsToInsert)
        .select();

      if (!attachmentError && attachmentResults) {
        newMessage.attachments = attachmentResults as Attachment[];
      }
    }

    // NEW: Mirror to OS VFS so it shows up in My Documents
    const chatForTitle = get().chats.find(c => c.id === chatId);
    if (chatForTitle) {
      await appendChatMessageToVFS(chatId, chatForTitle.title || 'Chat', newMessage);

      if (newMessage.attachments && newMessage.attachments.length > 0) {
        await saveChatAttachmentsToVFS(
          chatId,
          chatForTitle.title || 'Chat',
          newMessage.attachments.map(att => ({
            name: att.name,
            type: att.type,
            content: att.content
          }))
        );
      }
    }

    // Removed the local state update from here.
    // The useSupabaseChatStore will handle adding/updating messages in the local state.
    return newMessage;
  },

  addMessageLocally: (chatId, message) => {
    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === chatId
          ? {
              ...chat,
              messages: [...(chat.messages || []), message],
            }
          : chat
      ),
    }));
  },

  updateMessageLocally: (chatId, tempId, newMessage) => {
    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === chatId
          ? {
              ...chat,
              messages: (chat.messages || []).map(msg =>
                msg.id === tempId ? newMessage : msg
              ),
            }
          : chat
      ),
    }));
  },

  updateChatTitle: async (chatId, newTitle) => {
    // Capture old title before updating
    const current = get().chats.find(chat => chat.id === chatId);
    const oldTitle = current?.title || 'Chat';

    if (useAppStore.getState().saveHistory) {
      const { error } = await supabase
        .from('chats')
        .update({ title: newTitle })
        .eq('id', chatId);

      if (error) {
        console.error('Error updating chat title:', error);
        throw new Error(error.message);
      }
    }

    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === chatId ? { ...chat, title: newTitle } : chat
      ),
    }));

    // Rename the VFS folder to match the new chat title
    await renameChatVFSFolder(chatId, oldTitle, newTitle);
  },

  updateChat: (chatId, updates) => {
    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === chatId ? { ...chat, ...updates } : chat
      ),
    }));
  },

  updateChatModel: async (chatId, newModelId) => {
    if (useAppStore.getState().saveHistory) {
      const { error } = await supabase
        .from('chats')
        .update({ model_id: newModelId })
        .eq('id', chatId);

      if (error) {
        console.error('Error updating chat model:', error);
        throw new Error(error.message);
      }
    }

    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === chatId ? { ...chat, modelId: newModelId } : chat
      ),
    }));
  },

  deleteChat: async (chatId) => {
    if (useAppStore.getState().saveHistory) {
      const { error } = await supabase
        .from('chats')
        .delete()
        .eq('id', chatId);

      if (error) {
        console.error('Error deleting chat:', error);
        throw new Error(error.message);
      }
    }

    set(state => ({
      chats: state.chats.filter(chat => chat.id !== chatId),
    }));
  },

  deleteAllChats: async (userId) => {
    const { error } = await supabase
      .from('chats')
      .delete()
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting all chats:', error);
      toast({ title: "Error", description: "Failed to delete all of your chats.", variant: "destructive" });
      throw new Error(error.message);
    }

    set({ chats: [] });
    toast({ title: "Success", description: "All chat history has been deleted." });
  },

  addTokensUsed: async (chatId, tokens) => {
    if (useAppStore.getState().saveHistory) {
      const { error } = await supabase.rpc('add_tokens', {
        chat_id: chatId,
        tokens_to_add: tokens,
      });

      if (error) {
        console.error('Error adding tokens used:', error);
        throw new Error(error.message);
      }
    }

    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === chatId ? { ...chat, tokensUsed: (chat.tokensUsed || 0) + tokens } : chat
      ),
    }));
  },

  incrementChatViews: async (chatId) => {
    if (useAppStore.getState().saveHistory) {
      const { error } = await supabase.rpc('increment_chat_views', {
        chat_id: chatId,
      });

      if (error) {
        console.error('Error incrementing chat views:', error);
        throw new Error(error.message);
      }
    }
  },

  fetchModels: async () => {
    const { data, error } = await supabase
      .from('ai_models')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching models from Supabase:', error);
      toast({ title: "Error", description: "Failed to load AI models. Check console for details.", variant: "destructive" });
      return;
    }

    const supabaseModels: AIModel[] = data.map(m => ({
      id: m.id,
      name: m.name,
      provider: (m.provider || 'custom') as ModelProvider,
      description: m.description || '',
      context_length: m.context_length || undefined,
      is_active: m.is_active || false,
      capabilities: (m.capabilities && Array.isArray(m.capabilities) ? m.capabilities : []) as string[],
      tier: (m.tier || 'free') as 'free' | 'basic' | 'standard' | 'premium' | 'enterprise',
      apiName: m.apiName || m.name,
      logo_url: (m as any).logo_url || undefined,
      source: m.provider === 'ollama' ? 'remote' : undefined,
    }));

    // Combine with MaplePT virtual availability (ensures visibility even if not yet in DB)
    const mapleptModels: AIModel[] = [{
      id: 'maplept-mini',
      name: 'MaplePT-Mini',
      provider: 'maplept',
      description: 'MaplePT (vLLM) — Mini',
      capabilities: ['text', 'code'],
      tier: 'basic',
      apiName: '/app/canxpai-maplept-mini-v1',
      is_active: true,
    }];

    let ollamaModels: AIModel[] = [];
    try {
      const fetchedOllamaModels = await fetchOllamaModels();
      ollamaModels = fetchedOllamaModels.map(om => {
        const normalizedId = `ollama-${om.source}-${om.name.replace(/[:]/g, '-').replace(/\s/g, '')}`;
        return {
          id: normalizedId,
          name: om.name,
          provider: 'ollama',
          description: `Ollama model (${om.source})`,
          capabilities: [],
          tier: 'free',
          apiName: om.name,
          is_active: true,
          source: om.source,
        };
      });
    } catch (ollamaError) {
      console.error('Error fetching Ollama models:', ollamaError);
      toast({ title: "Warning", description: "Could not fetch Ollama models. Ensure Ollama server is running or proxy is configured.", variant: "warning" });
    }

    // Only add the virtual MaplePT model if there isn't a DB-backed one yet
    const hasMaplept = supabaseModels.some(m => m.provider === 'maplept');

    const combinedModelsMap = new Map<string, AIModel>();
    const toCombine = hasMaplept
      ? [...supabaseModels, ...ollamaModels]
      : [...supabaseModels, ...ollamaModels, ...mapleptModels];

    toCombine.forEach(model => {
      combinedModelsMap.set(model.id, model);
    });

    // NEW: Append MaplePT-Free local model so selection works globally
    const mapleptFreeId = 'maplept-free';
    if (!combinedModelsMap.has(mapleptFreeId)) {
      combinedModelsMap.set(mapleptFreeId, {
        id: mapleptFreeId,
        name: 'MaplePT-Free',
        provider: 'local', // uses LocalProvider in ModelService
        description: 'Free compute on your device.',
        capabilities: ['text', 'tools'],
        tier: 'free',
        apiName: 'TinyLlama-1.1b-chat-v1.0',
        is_active: true,
        logo_url: '/images/models/maplept.svg',
      } as AIModel);
    }

    set({
      models: Array.from(combinedModelsMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    });
  },

  updateModel: async (modelId, updates) => {
    const { error } = await supabase
      .from('ai_models')
      .update(updates)
      .eq('id', modelId);

    if (error) {
      console.error('Error updating model:', error);
      toast({ title: "Error", description: `Failed to update model: ${error.message}`, variant: "destructive" });
      throw new Error(error.message);
    }

    toast({ title: "Success", description: "Model updated successfully." });
    await get().fetchModels(); // Refresh models list
  },

  updateProviderModels: async (provider, updates) => {
    const { error } = await supabase
      .from('ai_models')
      .update(updates)
      .eq('provider', provider);

    if (error) {
      console.error(`Error updating models for provider ${provider}:`, error);
      toast({ title: "Error", description: `Failed to update models: ${error.message}`, variant: "destructive" });
      throw new Error(error.message);
    }

    toast({ title: "Success", description: `Models for ${provider} updated successfully.` });
    await get().fetchModels(); // Refresh models list
  },

  fetchProjects: async (userId) => {
    if (!userId) return;
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching projects:', error);
      toast({ title: "Error", description: "Failed to load projects.", variant: "destructive" });
      return;
    }

    set({ projects: data as Project[] });
  },

  addProject: async (name, description) => {
    const { user } = useAppStore.getState();
    if (!user) {
      toast({ title: "Authentication Error", description: "You must be logged in to create a project.", variant: "destructive" });
      return null;
    }

    const { data, error } = await supabase
      .from('projects')
      .insert({ user_id: user.id, name, description })
      .select()
      .single();

    if (error) {
      console.error('Error adding project:', error);
      toast({ title: "Error", description: "Failed to create new project.", variant: "destructive" });
      return null;
    }

    const newProject: Project = data as Project;
    set(state => ({ projects: [newProject, ...state.projects] }));
    return newProject;
  },

  updateProject: async (projectId, updates) => {
    const { error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', projectId);

    if (error) {
      console.error('Error updating project:', error);
      throw new Error(error.message);
    }

    set(state => ({
      projects: state.projects.map(project =>
        project.id === projectId ? { ...project, ...updates } : project
      ),
    }));
  },

  deleteProject: async (projectId) => {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) {
      console.error('Error deleting project:', error);
      throw new Error(error.message);
    }

    set(state => ({
      projects: state.projects.filter(project => project.id !== projectId),
    }));
  },

  assignChatToProject: async (chatId, projectId) => {
    if (useAppStore.getState().saveHistory) {
      const { error } = await supabase
        .from('chats')
        .update({ project_id: projectId })
        .eq('id', chatId);

      if (error) {
        console.error('Error assigning chat to project:', error);
        throw new Error(error.message);
      }
    }

    set(state => ({
      chats: state.chats.map(chat =>
        chat.id === chatId ? { ...chat, project_id: projectId } : chat
      ),
    }));
  },

  toggleFavoriteChat: async (chatId) => {
    const chat = get().chats.find(c => c.id === chatId);
    if (!chat) return;

    const newFavoriteStatus = !chat.isFavorite;

    if (useAppStore.getState().saveHistory) {
      const { error } = await supabase
        .from('chats')
        .update({ is_favorite: newFavoriteStatus })
        .eq('id', chatId);

      if (error) {
        console.error('Error toggling favorite:', error);
        throw new Error(error.message);
      }
    }

    set(state => ({
      chats: state.chats.map(c =>
        c.id === chatId ? { ...c, isFavorite: newFavoriteStatus } : c
      ),
    }));
  },

  fetchSingleChat: async (chatId) => {
    const { data: chatData, error: chatError } = await supabase
      .from('chats')
      .select(`
        id,
        title,
        created_at,
        updated_at,
        model_id,
        message_count,
        tokens_used,
        project_id,
        is_favorite,
        type,
        view_count,
        model_id_2
      `)
      .eq('id', chatId)
      .single();

    if (chatError) {
      console.error('Error fetching single chat:', chatError);
      toast({ title: "Error", description: "Failed to load chat details.", variant: "destructive" });
      return null;
    }

    const { data: messagesData, error: messagesError } = await supabase
      .from('messages')
      .select('*, attachments(*)')
      .eq('chat_id', chatId)
      .order('timestamp', { ascending: true });

    if (messagesError) {
      console.error('Error fetching messages for single chat:', messagesError);
      toast({ title: "Error", description: "Failed to load chat messages.", variant: "destructive" });
      return null;
    }

    const messages: Message[] = messagesData.map(msg => ({
      id: msg.id,
      chatId: msg.chat_id,
      content: msg.content,
      role: msg.role as 'user' | 'assistant' | 'system',
      sender: msg.sender as 'user' | 'ai',
      timestamp: msg.timestamp,
      model: msg.model || undefined,
      attachments: msg.attachments as Attachment[] || [],
      is_arena: msg.is_arena || false,
      arena_group_id: msg.arena_group_id || undefined,
      arena_model: msg.arena_model || undefined,
      arena_order: msg.arena_order || undefined,
    }));

    const singleChat: ChatWithMessages = {
      id: chatData.id,
      title: chatData.title,
      createdAt: chatData.created_at,
      updatedAt: chatData.updated_at,
      modelId: chatData.model_id,
      messageCount: chatData.message_count || 0,
      tokensUsed: chatData.tokens_used || 0,
      project_id: chatData.project_id,
      isFavorite: chatData.is_favorite,
      type: chatData.type as 'user-ai' | 'arena',
      viewCount: chatData.view_count,
      modelId2: chatData.model_id_2, // Include model_id_2 here
      messages: messages,
      messagesLoaded: true,
    };

    // Update the global chats state with this single chat
    set(state => {
      const existingChatIndex = state.chats.findIndex(c => c.id === singleChat.id);
      if (existingChatIndex > -1) {
        const updatedChats = [...state.chats];
        updatedChats[existingChatIndex] = singleChat;
        return { chats: updatedChats };
      } else {
        return { chats: [singleChat, ...state.chats] };
      }
    });

    return singleChat;
  },

  // MCP Service Implementations
  fetchMCPServices: async () => {
    const { data, error } = await supabase
      .from('mcp_services')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching MCP services:', error);
      toast({ title: "Error", description: "Failed to load MCP services.", variant: "destructive" });
      return [];
    }

    return data.map(s => ({
      id: s.id,
      name: s.name,
      description: s.description || '',
      url: s.url,
      icon: s.icon || 'cpu',
      tier: s.tier as SubscriptionTier,
      created_by: s.created_by || '',
      created_at: s.created_at,
    }));
  },

  addMCPService: async (service) => {
    const { user } = useAppStore.getState();
    if (!user) {
      toast({ title: "Authentication Error", description: "You must be logged in to add an MCP service.", variant: "destructive" });
      return null;
    }

    const { data, error } = await supabase
      .from('mcp_services')
      .insert({
        name: service.name,
        description: service.description,
        url: service.url,
        icon: service.icon,
        tier: service.tier,
        created_by: user.id,
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error adding MCP service:', error);
      toast({ title: "Error", description: "Failed to add MCP service.", variant: "destructive" });
      return null;
    }
    toast({ title: "Success", description: "MCP service added successfully." });
    return data.id;
  },

  updateMCPService: async (id, updates) => {
    const { error } = await supabase
      .from('mcp_services')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Error updating MCP service:', error);
      toast({ title: "Error", description: "Failed to update MCP service.", variant: "destructive" });
      throw new Error(error.message);
    }
    toast({ title: "Success", description: "MCP service updated successfully." });
  },

  deleteMCPService: async (id) => {
    const { error } = await supabase
      .from('mcp_services')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting MCP service:', error);
      toast({ title: "Error", description: "Failed to delete MCP service.", variant: "destructive" });
      throw new Error(error.message);
    }
    toast({ title: "Success", description: "MCP service deleted successfully." });
  },

  getUserMCPApiKey: async (serviceId) => {
    const { user } = useAppStore.getState();
    if (!user) return null;

    try {
      const { data, error } = await supabase.functions.invoke('get-user-mcp-key', {
        body: { serviceId },
      });

      if (error) {
        console.error('Error invoking get-user-mcp-key edge function:', error);
        toast({ title: "Error", description: "Failed to fetch API key via edge function.", variant: "destructive" });
        return null;
      }

      // The edge function returns the API key directly or null
      return data as string | null;
    } catch (error: any) {
      console.error('Exception calling get-user-mcp-key edge function:', error);
      toast({ title: "Error", description: `Failed to fetch API key: ${error.message}`, variant: "destructive" });
      return null;
    }
  },

  setUserMCPApiKey: async (serviceId, apiKey) => {
    const { user } = useAppStore.getState();
    if (!user) {
      toast({ title: "Authentication Error", description: "You must be logged in to save an API key.", variant: "destructive" });
      return;
    }

    const { error } = await supabase
      .from('user_mcp_keys')
      .upsert(
        { user_id: user.id, service_id: serviceId, api_key: apiKey },
        { onConflict: 'user_id,service_id' }
      );

    if (error) {
      console.error('Error setting user MCP API key:', error);
      toast({ title: "Error", description: "Failed to save API key.", variant: "destructive" });
      throw new Error(error.message);
    }
    toast({ title: "Success", description: "API key saved successfully." });
  },

  // Agent Implementations
  addAgent: async (agent, blueprintIds) => {
    const { user } = useAppStore.getState();
    if (!user) {
      toast({ title: "Authentication Error", description: "You must be logged in to create an agent.", variant: "destructive" });
      return null;
    }

    const { data, error } = await supabase
      .from('agents')
      .insert({
        user_id: user.id,
        name: agent.name,
        description: agent.description,
        instructions: agent.instructions,
        domain: agent.domain,
        rating: agent.rating,
        uses: agent.uses,
        is_standard: agent.is_standard,
        icon: agent.icon,
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding agent:', error);
      toast({ title: "Error", description: "Failed to create new agent.", variant: "destructive" });
      return null;
    }

    if (blueprintIds.length > 0) {
      const links = blueprintIds.map(bpId => ({
        agent_id: data.id,
        blueprint_id: bpId,
      }));
      const { error: linkError } = await supabase.from('agent_blueprints').insert(links);
      if (linkError) {
        console.error('Error linking blueprints to agent:', linkError);
        toast({ title: "Error", description: `Agent created, but failed to link blueprints: ${linkError.message}`, variant: "destructive" });
      }
    }

    toast({ title: "Success", description: "Agent created successfully!" });
    await get().fetchAgents();
    return data as Agent;
  },

  fetchAgents: async () => {
    const { user } = useAppStore.getState();
    if (!user) return;

    const { data: agentsWithBlueprints, error } = await supabase
      .from('agents')
      .select(`
        *,
        agent_blueprints (
          blueprints ( id, name, data )
        )
      `)
      .or(`user_id.eq.${user.id},is_standard.eq.true`);

    if (error) {
      console.error('Error fetching agents:', error);
      toast({ title: "Error", description: "Could not fetch agents.", variant: "destructive" });
      return;
    }

    const formattedAgents: AgentWithBlueprints[] = agentsWithBlueprints.map((agent: any) => ({
      ...agent,
      blueprints: agent.agent_blueprints.map((ab: any) => ab.blueprints).filter(Boolean)
    }));

    set({ agents: formattedAgents });
  },

  updateAgentBlueprints: async (agentId, blueprintIds) => {
    const { user } = useAppStore.getState();
    if (!user) return;

    const { data: agent, error: agentError } = await supabase
      .from('agents')
      .select('id')
      .eq('id', agentId)
      .eq('user_id', user.id)
      .single();

    if (agentError || !agent) {
      toast({ title: "Error", description: "You do not have permission to edit this agent.", variant: "destructive" });
      return;
    }

    const { error: deleteError } = await supabase
      .from('agent_blueprints')
      .delete()
      .eq('agent_id', agentId);

    if (deleteError) {
      toast({ title: "Error", description: `Failed to update blueprint links: ${deleteError.message}`, variant: "destructive" });
      return;
    }

    if (blueprintIds.length > 0) {
      const links = blueprintIds.map(bpId => ({
        agent_id: agentId,
        blueprint_id: bpId,
      }));
      const { error: insertError } = await supabase.from('agent_blueprints').insert(links);
      if (insertError) {
        toast({ title: "Error", description: `Failed to save new blueprint links: ${insertError.message}`, variant: "destructive" });
        return;
      }
    }

    toast({ title: "Success", description: "Agent blueprints updated." });
    await get().fetchAgents();
  },

  addBlueprint: async (blueprint) => {
    const { user } = useAppStore.getState();
    if (!user) {
      toast({ title: "Authentication Error", description: "You must be logged in to create a blueprint.", variant: "destructive" });
      return null;
    }

    const { data, error } = await supabase
      .from('blueprints')
      .insert({
        user_id: user.id,
        name: blueprint.name,
        description: blueprint.description,
        data: blueprint.data,
        is_private: false,
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding blueprint:', error);
      toast({ title: "Error", description: "Failed to create new blueprint.", variant: "destructive" });
      return null;
    }
    return data as Blueprint;
  },

  fetchAgentById: async (agentId) => {
    const { data, error } = await supabase
      .from('agents')
      .select(`
        *,
        agent_blueprints (
          blueprints ( id, name, data )
        )
      `)
      .eq('id', agentId)
      .single();

    if (error) {
      console.error(`Error fetching agent ${agentId}:`, error);
      return null;
    }

    const formattedAgent: AgentWithBlueprints = {
      ...data,
      blueprints: data.agent_blueprints.map((ab: any) => ab.blueprints).filter(Boolean)
    };

    return formattedAgent;
  },

  logOperatorAction: async (action, details) => {
    const { data: { session } = {} } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;

    const { error } = await supabase
      .from('os_operator_logs')
      .insert({ user_id: userId, action, details: details || {} });

    if (error) {
      console.error('[SupabaseStore] Failed to insert operator log:', error);
    }
  },

  fetchOperatorLogs: async (limit = 50) => {
    const { data: { session } = {} } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return [];

    const { data, error } = await supabase
      .from('os_operator_logs')
      .select('id, action, details, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('[SupabaseStore] Failed to fetch operator logs:', error);
      return [];
    }
    return (data || []).map(r => ({ id: r.id, action: r.action, details: r.details, created_at: r.created_at }));
  },
}));